import { motion } from "framer-motion";
import { ArrowLeft, HelpCircle, Zap, MessageCircle, Lock } from "lucide-react";

// All three feature chips share the SAME glow intensity & color for visual harmony.
const FEATURE_COLOR = "#9B6BFF";
const FEATURES = [
  { icon: Zap,           label: "Instant",     sub: "reactions" },
  { icon: MessageCircle, label: "Live",        sub: "answers"   },
  { icon: Lock,          label: "Private &",   sub: "secure"    },
];

// Decorative SVG sparkle/firework burst
function FireworkBurst({ color, size = 60, ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 60 60" {...rest}>
      <g style={{ filter: `drop-shadow(0 0 4px ${color})` }} stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none">
        <line x1="30" y1="6" x2="30" y2="20" />
        <line x1="30" y1="40" x2="30" y2="54" />
        <line x1="6" y1="30" x2="20" y2="30" />
        <line x1="40" y1="30" x2="54" y2="30" />
        <line x1="13" y1="13" x2="22" y2="22" />
        <line x1="38" y1="38" x2="47" y2="47" />
        <line x1="47" y1="13" x2="38" y2="22" />
        <line x1="22" y1="38" x2="13" y2="47" />
      </g>
    </svg>
  );
}

export default function PlayRemotelyEntry({ onCreateRoom, onJoinRoom, onBack }) {
  return (
    <div className="vc-bg min-h-screen relative overflow-hidden" data-testid="remotely-entry-screen">
      {/* Top bar */}
      <div className="relative z-[10] flex items-center justify-between px-5 pt-5">
        <button
          onClick={onBack}
          data-testid="remotely-entry-back"
          aria-label="Back"
          className="w-10 h-10 rounded-full flex items-center justify-center text-white/80 hover:text-white transition active:scale-95"
          style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.08)" }}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <button
          aria-label="Help"
          className="w-10 h-10 rounded-full flex items-center justify-center text-white/55 hover:text-white transition active:scale-95"
          style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.08)" }}
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      </div>

      <main className="relative z-[5] max-w-md mx-auto px-5 pt-3 pb-16">
        {/* Hero bloom */}
        <div
          className="hero-bloom absolute pointer-events-none rounded-full"
          style={{
            top: 30, left: "50%", transform: "translateX(-50%)",
            width: 460, height: 460,
            background: "radial-gradient(circle, rgba(155, 107, 255, 0.45) 0%, rgba(255, 46, 154, 0.18) 35%, transparent 70%)",
            filter: "blur(40px)", zIndex: 0,
          }}
          aria-hidden
        />

        {/* Decorative firework sparkles around title */}
        <FireworkBurst color="#9B6BFF" size={48} className="absolute z-[3]" style={{ top: 28, left: 8 }} />
        <FireworkBurst color="#FF2E9A" size={40} className="absolute z-[3]" style={{ top: 60, right: 4 }} />
        <FireworkBurst color="#FF6B47" size={32} className="absolute z-[3]" style={{ top: 16, right: 60 }} />
        <FireworkBurst color="#FF2E9A" size={28} className="absolute z-[3]" style={{ top: 90, left: 30 }} />

        {/* Headline */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-[4] text-center mb-4 mt-2"
        >
          <h1
            className="text-white font-extrabold leading-[0.92] tracking-tight"
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: "clamp(2.6rem, 13vw, 3.6rem)",
              letterSpacing: "-0.04em",
            }}
            data-testid="remotely-entry-title"
          >
            LONG
            <br />
            <span
              style={{
                background: "linear-gradient(90deg, #E0D6FF 0%, #9B6BFF 55%, #6A4DFF 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                textShadow: "0 0 1px rgba(155, 107, 255, 0.6)",
                filter: "drop-shadow(0 1px 1.5px rgba(0, 0, 0, 0.55))",
              }}
            >
              DISTANCE
            </span>
          </h1>
          <p
            className="mt-3"
            style={{
              fontSize: "15px",
              lineHeight: "1.45",
              fontWeight: 600,
              color: "#F4EAFF",
              textShadow: "0 0 14px rgba(155, 107, 255, 0.45)",
            }}
            data-testid="remotely-entry-subtitle"
          >
            Real connection knows{" "}
            <span
              style={{
                color: "#FF7BC0",
                textShadow: "0 0 14px rgba(255, 46, 154, 0.55)",
              }}
            >
              no distance 💜
            </span>
          </p>
        </motion.div>

        {/* CINEMATIC HERO — phones extend toward the edges */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto mb-5"
          style={{ width: "85%", height: "32vh", overflow: "hidden" }}
        >
          <img
            src="/assets/home/hero-longdistance-v2.png"
            alt=""
            className="w-full h-full object-cover"
            style={{
              filter: "brightness(1.15) saturate(1.1) drop-shadow(0 0 32px rgba(155, 107, 255, 0.6)) drop-shadow(0 0 70px rgba(155, 107, 255, 0.32))",
            }}
          />
        </motion.div>

        {/* Feature icons — 3-col cinematic chips (same glow intensity across all 3) */}
        <section className="mb-8">
          <div className="grid grid-cols-3 gap-3" data-testid="remotely-features">
            {FEATURES.map((f) => (
              <div
                key={f.label + f.sub}
                className="flex flex-col items-center text-center px-2"
              >
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center mb-2.5"
                  style={{
                    background: `${FEATURE_COLOR}1f`,
                    border: `1.5px solid ${FEATURE_COLOR}70`,
                    boxShadow: `0 0 16px ${FEATURE_COLOR}66, inset 0 0 12px ${FEATURE_COLOR}22`,
                  }}
                >
                  <f.icon
                    className="w-6 h-6"
                    style={{ color: FEATURE_COLOR, filter: `drop-shadow(0 0 5px ${FEATURE_COLOR})` }}
                    strokeWidth={2}
                    fill={f.icon === Zap ? FEATURE_COLOR : "none"}
                  />
                </div>
                <span className="text-white text-[12px] font-bold leading-tight">{f.label}</span>
                <span className="text-[#B8B8CC] text-[11.5px] leading-tight mt-0.5" style={{ fontWeight: 400 }}>{f.sub}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Single primary CTA — Long distance */}
        <section className="space-y-3 mb-4" data-testid="remotely-share-actions">
          <button
            onClick={() => onCreateRoom?.()}
            data-testid="remotely-entry-play"
            className="vc-cta vc-cta-primary w-full rounded-full py-[18px] font-extrabold flex items-center justify-center gap-2.5"
            style={{ fontSize: "17px", letterSpacing: "0.01em" }}
          >
            Let's go!
            <span aria-hidden style={{ fontSize: "18px", lineHeight: 1 }}>🔥</span>
          </button>
        </section>

      </main>
    </div>
  );
}
